if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.pinchZoom = {
          pointers: new Map(),
          scale: 1,
          translateX: 0,
          translateY: 0,
          lastTapTime: 0,
        };
      }

      hide() {
        this.resetPinchZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.setupPinchZoom(activeMedia);
        this.openMobileImageZoom(activeMedia);
      }

      openMobileImageZoom(activeMedia) {
        if (!this.isMobileModalZoom() || !activeMedia?.matches('img')) return;

        window.requestAnimationFrame(() => {
          const image = this.getActiveZoomImage();
          if (image !== activeMedia || !this.hasAttribute('open') || this.pinchZoom.scale > 1) return;

          this.pinchZoom.scale = 2;
          this.pinchZoom.translateX = 0;
          this.pinchZoom.translateY = 0;
          this.applyPinchZoom(image);
        });
      }

      setupPinchZoom(activeMedia) {
        const image = activeMedia?.matches('img') ? activeMedia : null;
        const container = this.querySelector('[role="document"]');
        if (!image || !container || image.dataset.lumiyearPinchZoomReady === 'true') return;

        image.dataset.lumiyearPinchZoomReady = 'true';
        image.addEventListener('pointerdown', this.onPinchPointerDown.bind(this));
        image.addEventListener('pointermove', this.onPinchPointerMove.bind(this));
        image.addEventListener('pointerup', this.onPinchPointerEnd.bind(this));
        image.addEventListener('pointercancel', this.onPinchPointerEnd.bind(this));
        image.addEventListener('lostpointercapture', this.onPinchPointerEnd.bind(this));
        image.addEventListener('dblclick', (event) => {
          if (!this.isMobileModalZoom()) return;
          event.preventDefault();
          this.togglePinchZoom(event.clientX, event.clientY);
        });
      }

      isMobileModalZoom() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      getActiveZoomImage() {
        const activeMediaId = this.openedBy?.getAttribute('data-media-id');
        if (!activeMediaId) return null;
        const activeMedia = this.querySelector(`img[data-media-id="${activeMediaId}"].active`);
        return activeMedia || this.querySelector(`img[data-media-id="${activeMediaId}"]`);
      }

      onPinchPointerDown(event) {
        if (!this.isMobileModalZoom() || event.pointerType !== 'touch') return;

        const image = this.getActiveZoomImage();
        if (event.currentTarget !== image) return;

        event.preventDefault();
        image.setPointerCapture?.(event.pointerId);
        this.pinchZoom.pointers.set(event.pointerId, {
          clientX: event.clientX,
          clientY: event.clientY,
        });

        if (this.pinchZoom.pointers.size === 1) {
          const now = Date.now();
          if (now - this.pinchZoom.lastTapTime < 280) {
            event.preventDefault();
            this.togglePinchZoom(event.clientX, event.clientY);
            this.pinchZoom.lastTapTime = 0;
          } else {
            this.pinchZoom.lastTapTime = now;
          }

          this.pinchZoom.dragStartX = event.clientX;
          this.pinchZoom.dragStartY = event.clientY;
          this.pinchZoom.startTranslateX = this.pinchZoom.translateX;
          this.pinchZoom.startTranslateY = this.pinchZoom.translateY;
        }

        if (this.pinchZoom.pointers.size === 2) {
          event.preventDefault();
          const points = Array.from(this.pinchZoom.pointers.values());
          this.pinchZoom.startDistance = this.getPinchDistance(points[0], points[1]);
          this.pinchZoom.startMidpoint = this.getPinchMidpoint(points[0], points[1]);
          this.pinchZoom.startScale = this.pinchZoom.scale;
          this.pinchZoom.startTranslateX = this.pinchZoom.translateX;
          this.pinchZoom.startTranslateY = this.pinchZoom.translateY;
          image.classList.add('is-pinching');
        }
      }

      onPinchPointerMove(event) {
        if (!this.isMobileModalZoom() || event.pointerType !== 'touch' || !this.pinchZoom.pointers.has(event.pointerId)) return;

        const image = this.getActiveZoomImage();
        if (!image) return;

        this.pinchZoom.pointers.set(event.pointerId, {
          clientX: event.clientX,
          clientY: event.clientY,
        });

        if (this.pinchZoom.pointers.size === 2) {
          event.preventDefault();
          const points = Array.from(this.pinchZoom.pointers.values());
          const currentDistance = this.getPinchDistance(points[0], points[1]);
          const currentMidpoint = this.getPinchMidpoint(points[0], points[1]);
          const nextScale = this.clamp(this.pinchZoom.startScale * (currentDistance / this.pinchZoom.startDistance), 0.5, 4);

          this.pinchZoom.scale = nextScale;
          this.pinchZoom.translateX =
            this.pinchZoom.startTranslateX + (currentMidpoint.clientX - this.pinchZoom.startMidpoint.clientX);
          this.pinchZoom.translateY =
            this.pinchZoom.startTranslateY + (currentMidpoint.clientY - this.pinchZoom.startMidpoint.clientY);
          this.applyPinchZoom(image);
          return;
        }

        if (this.pinchZoom.pointers.size === 1 && this.pinchZoom.scale > 1) {
          event.preventDefault();
          this.pinchZoom.translateX = this.pinchZoom.startTranslateX + event.clientX - this.pinchZoom.dragStartX;
          this.pinchZoom.translateY = this.pinchZoom.startTranslateY + event.clientY - this.pinchZoom.dragStartY;
          this.applyPinchZoom(image);
        }
      }

      onPinchPointerEnd(event) {
        if (!this.pinchZoom.pointers.has(event.pointerId)) return;

        const image = this.getActiveZoomImage();
        this.pinchZoom.pointers.delete(event.pointerId);
        image?.classList.remove('is-pinching');

        if (this.pinchZoom.pointers.size === 1) {
          const point = Array.from(this.pinchZoom.pointers.values())[0];
          this.pinchZoom.dragStartX = point.clientX;
          this.pinchZoom.dragStartY = point.clientY;
          this.pinchZoom.startTranslateX = this.pinchZoom.translateX;
          this.pinchZoom.startTranslateY = this.pinchZoom.translateY;
        }
      }

      togglePinchZoom(clientX, clientY) {
        const image = this.getActiveZoomImage();
        if (!image) return;

        if (this.pinchZoom.scale !== 1) {
          this.resetPinchZoom();
          return;
        }

        const rect = image.getBoundingClientRect();
        this.pinchZoom.scale = 2;
        this.pinchZoom.translateX = (rect.left + rect.width / 2 - clientX) * 0.5;
        this.pinchZoom.translateY = (rect.top + rect.height / 2 - clientY) * 0.5;
        this.applyPinchZoom(image);
      }

      applyPinchZoom(image) {
        if (!image) return;

        const container = this.querySelector('[role="document"]');
        const containerRect = container?.getBoundingClientRect();
        const imageRect = image.getBoundingClientRect();
        const baseWidth = imageRect.width / this.pinchZoom.scale;
        const baseHeight = imageRect.height / this.pinchZoom.scale;
        const viewport = containerRect || {
          left: 0,
          right: window.innerWidth,
          top: 0,
          bottom: window.innerHeight,
        };
        const scaledWidth = baseWidth * this.pinchZoom.scale;
        const scaledHeight = baseHeight * this.pinchZoom.scale;
        const baseCenterX = imageRect.left - this.pinchZoom.translateX + scaledWidth / 2;
        const baseCenterY = imageRect.top - this.pinchZoom.translateY + scaledHeight / 2;

        // Calculate each edge from the image's actual modal position. This
        // keeps panning available when Safari places a zoomed-out image below
        // the centre of the visible modal area.
        const leftEdgeOffset = viewport.left - (baseCenterX - scaledWidth / 2);
        const rightEdgeOffset = viewport.right - (baseCenterX + scaledWidth / 2);
        const topEdgeOffset = viewport.top - (baseCenterY - scaledHeight / 2);
        const bottomEdgeOffset = viewport.bottom - (baseCenterY + scaledHeight / 2);

        this.pinchZoom.translateX = this.clamp(
          this.pinchZoom.translateX,
          Math.min(leftEdgeOffset, rightEdgeOffset),
          Math.max(leftEdgeOffset, rightEdgeOffset)
        );
        this.pinchZoom.translateY = this.clamp(
          this.pinchZoom.translateY,
          Math.min(topEdgeOffset, bottomEdgeOffset),
          Math.max(topEdgeOffset, bottomEdgeOffset)
        );
        image.style.setProperty('--lumiyear-modal-zoom-scale', this.pinchZoom.scale);
        image.style.setProperty('--lumiyear-modal-zoom-x', `${this.pinchZoom.translateX}px`);
        image.style.setProperty('--lumiyear-modal-zoom-y', `${this.pinchZoom.translateY}px`);
        image.classList.add('is-zoomed');
      }

      resetPinchZoom() {
        const image = this.getActiveZoomImage();
        this.pinchZoom.pointers.clear();
        this.pinchZoom.scale = 1;
        this.pinchZoom.translateX = 0;
        this.pinchZoom.translateY = 0;

        if (!image) return;
        image.classList.remove('is-zoomed', 'is-pinching');
        image.style.removeProperty('--lumiyear-modal-zoom-scale');
        image.style.removeProperty('--lumiyear-modal-zoom-x');
        image.style.removeProperty('--lumiyear-modal-zoom-y');
      }

      getPinchDistance(firstPoint, secondPoint) {
        return Math.hypot(firstPoint.clientX - secondPoint.clientX, firstPoint.clientY - secondPoint.clientY);
      }

      getPinchMidpoint(firstPoint, secondPoint) {
        return {
          clientX: (firstPoint.clientX + secondPoint.clientX) / 2,
          clientY: (firstPoint.clientY + secondPoint.clientY) / 2,
        };
      }

      clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
      }
    }
  );
}
